import { useEffect } from 'react';
import { useMatches } from 'react-router-dom';
import { safeDisplayText } from '@steward-app/steward-types';
import { useDynamicCrumbValue } from './Breadcrumbs';
import { useBrand } from '../brand/BrandProvider';

interface RouteCrumbHandle {
  crumb?: string;
}

/**
 * Keeps the browser tab title in sync with the current route:
 *   "<page> · Steward"  — on any page, and
 *   "Steward"           — on the dashboard/root.
 *
 * The page name is the deepest `handle.crumb` (same source as the breadcrumbs,
 * so the two never drift); detail pages prefer their dynamic record name (e.g.
 * a document or account title). Product-last so the most identifying word
 * survives tab truncation (docs/01-design.md brand note). Renders nothing.
 */
export function DocumentTitle() {
  const matches = useMatches();
  const dynamic = useDynamicCrumbValue();
  const { productName: APP_NAME } = useBrand();

  const staticCrumbs = matches
    .map(m => (m.handle as RouteCrumbHandle | undefined)?.crumb)
    .filter((c): c is string => Boolean(c));
  const page = dynamic ?? staticCrumbs[staticCrumbs.length - 1];
  const isRoot = !dynamic && matches[matches.length - 1]?.pathname === '/';

  useEffect(() => {
    // The browser tab is a string, not markup, so there is no `<bdi>` to put around a
    // record's name here — the only way to keep an override out of the tab title is to
    // remove it. The façade strips these on the way out, so this is the same
    // belt-and-braces the download sink got in #525: this value can come from a React
    // Query cache an older bundle filled (steward-app#600).
    const name = page === undefined ? undefined : safeDisplayText(page);
    document.title = isRoot || !name ? APP_NAME : `${name} · ${APP_NAME}`;
  }, [isRoot, page, APP_NAME]);

  // Reset to the bare brand when the app shell unmounts (e.g. on sign-out,
  // which navigates to the standalone /login route).
  useEffect(() => () => { document.title = APP_NAME; }, [APP_NAME]);

  return null;
}
